'use client';

import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useState } from 'react';
import type { IGame } from '~/types/api';
import {
  GAME_STATES,
  ISSUE_STATUSES,
  fetchGameStatus,
} from '~/utils/gameState';

interface GameStatusProps {
  game: IGame;
}

// Game status constants
const GAME_STATUS = GAME_STATES;

export function GameStatus({ game }: GameStatusProps) {
  // Add local state to track current status
  const [localStatus, setLocalStatus] = useState(game.recordStatus);
  const isMakeupGame = game?.cancelledGame !== null;
  const gameId = game.gameId.toString();

  // Log initial status for debugging
  // console.log(
  //   `GameStatus rendering: gameId=${gameId}, initial status=${localStatus}`,
  // );

  // Only poll for live games or scheduled games that should be starting soon
  const shouldPoll = useMemo(() => {
    // Always poll for in-progress games
    if (localStatus === GAME_STATES.IN_PROGRESS) return true;

    // Poll for scheduled games that are starting soon (within 1 hour)
    if (localStatus === GAME_STATES.SCHEDULED && game.gameDateIso) {
      const gameTime = DateTime.fromISO(game.gameDateIso || '');
      const now = DateTime.now();
      const diffInHours = gameTime.diff(now, 'hours').hours;
      return diffInHours >= 0 && diffInHours < 1;
    }

    return false;
  }, [localStatus, game.gameDateIso]);

  // Use React Query for polling
  const { data: liveStatus } = useQuery({
    queryKey: ['gameStatus', gameId],
    queryFn: () => fetchGameStatus(game),
    enabled: shouldPoll,
    refetchInterval: shouldPoll ? 30000 : false, // Poll every 30 seconds if needed
    staleTime: 10000,
  });

  // Update local status when liveStatus changes
  useEffect(() => {
    if (liveStatus) {
      setLocalStatus(liveStatus);
    }
  }, [liveStatus]);

  // Use the local status which will be updated by liveStatus
  const currentStatus = localStatus;

  // Log when status changes for debugging
  // console.log(`Game ${gameId} final status: ${currentStatus}`);

  const gameDateTime = DateTime.fromISO(
    game?.cancelledGame?.gameDateIso ?? '',
  ).toLocal();
  const date = gameDateTime.isValid
    ? gameDateTime.toFormat('MMMM d, yyyy', { locale: 'en-US' })
    : '';
  const time = gameDateTime.isValid
    ? gameDateTime.toFormat('h:mm a ZZZZ', { locale: 'en-US' })
    : '';

  return (
    <>
      <div
        className={clsx(
          'my-4 rounded-sm bg-[#DB1500] px-2 text-sm font-bold uppercase [&_span]:leading-[20px]',
          currentStatus === GAME_STATES.IN_PROGRESS && '!bg-white !text-black',
          isMakeupGame && '!bg-[#FFF3B3] !text-black',
        )}
      >
        {currentStatus === GAME_STATES.IN_PROGRESS && (
          <div className="flex items-center gap-x-1">
            <span className="animate-live-pulse font-roboto text-[30px] text-[#DB1500]">
              •
            </span>
            <span className="font-roboto text-[12px] font-medium">LIVE</span>
          </div>
        )}
        {ISSUE_STATUSES.includes(currentStatus as any) && (
          <div className="flex items-center gap-x-1">
            <span>{currentStatus}</span>
          </div>
        )}
        {isMakeupGame && (
          <div className="flex items-center gap-x-1">
            <span>Makeup Game</span>
          </div>
        )}
      </div>
      {isMakeupGame && (
        <p className="text-3 text-center font-medium normal-case max-lg:mt-4 lg:px-4 lg:pb-4">
          Makeup of postponed game on {date || 'TBA'} at {time || 'TBA'}
        </p>
      )}
    </>
  );
}
